// computeCompactCardData — Pure function that computes the data needed for
// the CompactTripCard view in the trips list.
//
// Takes trip data, flight statuses, departure calculations, and the current
// time, and returns a CompactCardData object with trip name, date range,
// current stage label, next action summary, progress, and alerts.
//
// Validates: Requirements 9.1, 9.3, 9.4, 9.5, 9.6

import { classifyStageNodes } from './classifyStageNodes';
import { computeNextAction } from '../../services/nextActionEngine';
import { STAGE_LABELS, STAGES } from '../../services/journeyStageEngine';

/**
 * @typedef {import('../../types/tripcard').CompactCardData} CompactCardData
 * @typedef {import('../../types/tripcard').AlertBadge} AlertBadge
 */

// ── Constants ───────────────────────────────────────────────

const MAX_SUMMARY_LENGTH = 80;

// ── Helpers ─────────────────────────────────────────────────

/**
 * Truncates a string to the max summary length, preserving word boundaries.
 *
 * @param {string} text - Text to truncate
 * @returns {string} Truncated text ≤80 characters
 */
function truncateToMaxLength(text) {
  if (!text || text.length <= MAX_SUMMARY_LENGTH) return text || '';

  const truncated = text.slice(0, MAX_SUMMARY_LENGTH);
  const lastSpace = truncated.lastIndexOf(' ');

  if (lastSpace > MAX_SUMMARY_LENGTH * 0.5) {
    return truncated.slice(0, lastSpace);
  }

  return truncated;
}

/**
 * Parses a date value into a Date object safely.
 *
 * @param {string|Date|undefined} value - Date value to parse
 * @returns {Date|null}
 */
function safeParseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Determines the current stage label from the active stage in the classified nodes.
 *
 * @param {Array} stageNodes - Classified stage nodes
 * @returns {string} Human-readable stage label
 */
function resolveCurrentStageLabel(stageNodes) {
  const activeNode = stageNodes.find((node) => node.stageState === 'active');

  if (activeNode) {
    return STAGE_LABELS[activeNode.stage] || activeNode.stage;
  }

  // If all stages are completed, show "Trip Complete"
  const allCompleted = stageNodes.length > 0 &&
    stageNodes.every((node) => node.stageState === 'completed');

  if (allCompleted) {
    return STAGE_LABELS[STAGES.TRIP_COMPLETE] || 'Trip Complete';
  }

  // If no active stage, find the first upcoming stage
  const firstUpcoming = stageNodes.find((node) => node.stageState === 'upcoming');
  if (firstUpcoming) {
    return STAGE_LABELS[STAGES.PRE_TRIP] || 'Upcoming Trip';
  }

  return 'Upcoming Trip';
}

/**
 * Computes progress as completed stages / total stages with events.
 *
 * @param {Array} stageNodes - Classified stage nodes
 * @returns {{ completed: number, total: number }}
 */
function computeProgress(stageNodes) {
  const total = stageNodes.length;
  const completed = stageNodes.filter((node) => node.stageState === 'completed').length;

  return { completed, total };
}

/**
 * Collects all alerts from stage nodes, prioritizing critical over warning.
 *
 * @param {Array} stageNodes - Classified stage nodes
 * @returns {Array<AlertBadge>}
 */
function collectAlerts(stageNodes) {
  /** @type {Array<AlertBadge>} */
  const alerts = [];

  for (const node of stageNodes) {
    if (node.alert) {
      alerts.push({
        type: node.alert.type,
        source: node.alert.source,
        message: node.alert.message,
      });
    }
  }

  // Sort: critical first, then warning
  alerts.sort((a, b) => {
    if (a.type === 'critical' && b.type !== 'critical') return -1;
    if (a.type !== 'critical' && b.type === 'critical') return 1;
    return 0;
  });

  return alerts;
}

/**
 * Determines whether the trip is entirely in the past (all stages completed).
 *
 * @param {Array} stageNodes - Classified stage nodes
 * @returns {boolean}
 */
function isTripCompleted(stageNodes) {
  return stageNodes.length > 0 &&
    stageNodes.every((node) => node.stageState === 'completed');
}

/**
 * Determines whether the trip is entirely in the future (no stages started).
 *
 * @param {Array} stageNodes - Classified stage nodes
 * @returns {boolean}
 */
function isTripFuture(stageNodes) {
  return stageNodes.length > 0 &&
    stageNodes.every((node) => node.stageState === 'upcoming');
}

/**
 * Computes the next action summary string based on the trip's segment classification.
 * Extracted to reduce cyclomatic complexity of computeCompactCardData.
 *
 * @param {Array} stageNodes - Classified stage nodes
 * @param {Object|null} activeStageResult - Full active stage result
 * @param {Array} safeEvents - Safe events array
 * @param {Date} safeNow - Safe current time
 * @returns {string} Truncated next action summary
 */
function computeNextActionSummary(stageNodes, activeStageResult, safeEvents, safeNow) {
  if (isTripCompleted(stageNodes)) {
    return '';
  }

  // Both future and active trips compute next action the same way
  const nextAction = computeNextAction(activeStageResult || null, safeEvents, safeNow);
  return truncateToMaxLength(
    nextAction.isIdle ? (nextAction.idleMessage || '') : nextAction.instruction
  );
}

// ── Main ────────────────────────────────────────────────────

/**
 * Computes the data needed for the CompactTripCard view.
 *
 * @param {Object} params
 * @param {Object} params.trip - Trip data object with tripName, startDate, endDate, flights, etc.
 * @param {Array<Object>} params.events - ClassifiedEvent objects from buildJourneyEvents
 * @param {string|null} params.activeStageId - The currently active stage ID (e.g. 'in_flight')
 * @param {Object|null} [params.activeStageResult] - Full active stage result from resolveTripStages
 * @param {Date} params.now - Current time
 * @returns {CompactCardData}
 */
export function computeCompactCardData({ trip, events, activeStageId, activeStageResult, now }) {
  const safeNow = now instanceof Date && !Number.isNaN(now.getTime())
    ? now
    : new Date();

  const safeEvents = Array.isArray(events) ? events : [];

  // Compute trip name
  const tripName = trip?.tripName || trip?.name || 'Untitled Trip';

  // Compute date range
  const start = safeParseDate(trip?.startDate) || safeNow;
  const end = safeParseDate(trip?.endDate) || start;
  const dateRange = { start, end };

  // Classify stage nodes to determine progress and alerts
  const stageNodes = classifyStageNodes(safeEvents, activeStageId, safeNow);

  // Determine current stage label
  const currentStageLabel = resolveCurrentStageLabel(stageNodes);

  // Compute progress
  const progress = computeProgress(stageNodes);

  // Collect alerts
  const alerts = collectAlerts(stageNodes);

  // Compute next action summary via helper
  const nextActionSummary = computeNextActionSummary(stageNodes, activeStageResult, safeEvents, safeNow);

  return {
    tripName,
    dateRange,
    currentStageLabel,
    nextActionSummary,
    progress,
    alerts,
  };
}

export default computeCompactCardData;
